import { useLayoutEffect, useRef, useState } from "react";
import {
  motion,
  useAnimation,
  useMotionValue,
  useTransform,
  useDragControls,
} from "framer-motion";

// Map-first draggable bottom sheet with three snap points (expanded / partial /
// collapsed). Drag is initiated from the handle only so the inner list scrolls
// normally.
// - Bottom is pinned to the wrapper bottom (height = wrapperHeight - y) so the
//   full scrollable content is always reachable above the bottom nav.
// - Wrapper bottom is set to the MEASURED bottom-nav height so the sheet visually
//   meets the top of the nav (no map gap) while respecting the iPhone safe area
//   (the nav is fixed at the very bottom).
// - `topInset` (px, measured top search/category controls height) caps how far up
//   the expanded sheet can travel so its handle never hides behind the controls.
// The originating page stays mounted while EventDetails is shown as an overlay, so
// the drawer position and inner list scroll are preserved in the DOM on Back — no
// scroll/position reconstruction is needed here.
export function BottomSheet({ children, topInset = 190, collapseSignal = 0 }) {
  const wrapRef = useRef(null);
  const scrollRef = useRef(null);
  const wrapHRef = useRef(0);
  const controls = useAnimation();
  const y = useMotionValue(9999);
  const height = useTransform(y, (v) => Math.max(80, wrapHRef.current - v));
  const dragControls = useDragControls();
  const [snaps, setSnaps] = useState(null);
  const [navH, setNavH] = useState(84);
  const started = useRef(false);

  const spring = { type: "spring", stiffness: 340, damping: 36 };
  const snapTo = (target) => controls.start({ y: target, transition: spring });

  // Measure the fixed bottom navigation so the sheet meets its top edge exactly.
  // A ResizeObserver + fonts.ready catch first-load height changes (web-font
  // settling, viewport/URL-bar init) that never fire a window `resize`.
  useLayoutEffect(() => {
    let ro;
    const measureNav = () => {
      const nav = document.querySelector('[data-testid="bottom-nav"]');
      if (nav) setNavH(Math.round(nav.getBoundingClientRect().height));
    };
    measureNav();
    const nav = document.querySelector('[data-testid="bottom-nav"]');
    if (nav && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measureNav);
      ro.observe(nav);
    }
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measureNav).catch(() => {});
    }
    window.addEventListener("resize", measureNav);
    return () => {
      window.removeEventListener("resize", measureNav);
      if (ro) ro.disconnect();
    };
  }, []);

  // Compute snap points (recompute when nav height or top controls change).
  // A ResizeObserver on the wrapper keeps wrapHRef/snaps accurate as the actual
  // drawer height settles on first load, so the drawer bottom stays flush.
  useLayoutEffect(() => {
    const compute = () => {
      const H = wrapRef.current ? wrapRef.current.clientHeight : window.innerHeight - navH;
      wrapHRef.current = H;
      const expanded = Math.round(topInset) + 12; // stop just below the top controls
      const collapsedPeek = 116; // compact peek: handle + a small sliver of header
      const collapsed = Math.max(H - collapsedPeek, expanded + 70);
      // Keep the MIDDLE position unchanged (previous 220px reference peek).
      const midReference = Math.max(H - 220, expanded + 90);
      const partial = Math.round((expanded + midReference) / 2);
      setSnaps({ expanded, partial, collapsed });
      if (!started.current) {
        y.set(collapsed); // map is the focus on a fresh visit
        started.current = true;
      } else {
        y.set(y.get()); // re-run height transform after a resize
      }
    };
    compute();
    let ro;
    if (wrapRef.current && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(compute);
      ro.observe(wrapRef.current);
    }
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(compute).catch(() => {});
    }
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("resize", compute);
      if (ro) ro.disconnect();
    };
  }, [y, navH, topInset]);

  const onDragEnd = (_, info) => {
    if (!snaps) return;
    const { expanded, partial, collapsed } = snaps;
    const projected = y.get() + info.velocity.y * 0.18;
    const nearest = [expanded, partial, collapsed].reduce((a, b) =>
      Math.abs(b - projected) < Math.abs(a - projected) ? b : a
    );
    snapTo(nearest);
  };

  // Collapse the drawer on demand (e.g. the country changed on Discover) and reset
  // the inner list back to the top, so reopening the drawer starts at "Happening
  // near you" for the new country instead of the previous country's scroll offset.
  // Skips the initial value so it never fights the default position on mount. This
  // only fires on a deliberate country change (collapseSignal) — EventDetails → Back
  // keeps Discover mounted with its scroll untouched.
  useLayoutEffect(() => {
    if (collapseSignal === 0) return;
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    if (snaps) snapTo(snaps.collapsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseSignal]);

  return (
    <div
      ref={wrapRef}
      className="pointer-events-none fixed inset-x-0 top-0 z-[1200] mx-auto max-w-md overflow-hidden"
      style={{ bottom: `${navH}px` }}
    >
      <motion.div
        data-testid="event-drawer"
        drag="y"
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={{
          top: snaps ? snaps.expanded : 0,
          bottom: snaps ? snaps.collapsed : 9999,
        }}
        dragElastic={0.05}
        animate={controls}
        onDragEnd={onDragEnd}
        style={{ y, height }}
        className="pointer-events-auto absolute inset-x-0 top-0 flex select-none flex-col rounded-t-[28px] border-t border-white/10 bg-[#0a0a0a] shadow-[0_-24px_60px_rgba(0,0,0,0.75)]"
      >
        <div
          data-testid="drawer-handle"
          onPointerDown={(e) => dragControls.start(e)}
          className="flex shrink-0 cursor-grab touch-none items-center justify-center pb-2 pt-3.5 active:cursor-grabbing"
        >
          <div className="h-1.5 w-11 rounded-full bg-white/25" />
        </div>
        <div className="hide-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain pb-6" ref={scrollRef}>
          {children}
        </div>
      </motion.div>
    </div>
  );
}
